import { useState, useEffect, useMemo, useRef } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import axios from "axios";
import toast from "react-hot-toast";
import { EyeO, ClosedEye } from "@react-vant/icons";

import { Autocomplete, TextField } from "@mui/material";

import logo from "../../assets/images/logo/logo.png";
import { endpoint } from "../../services/urls";
import { country_code } from "../../utils/countryCode";

function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refFromUrl = searchParams.get("code") || "";

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState("");

  const [sponsorName, setSponsorName] = useState("");
  const [sponsorLoading, setSponsorLoading] = useState(false);

  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpFocused, setOtpFocused] = useState(false);

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryCode, setCountryCode] = useState("");

  const [pinDigits, setPinDigits] = useState(["", "", "", "", ""]);
  const pinRefs = useRef([]);

  const countryOptions = useMemo(() => {
    const seen = new Set();
    return country_code
      .filter((item) => {
        if (seen.has(item.cca2)) return false;
        seen.add(item.cca2);
        return true;
      })
      .map((item) => ({
        cca2: item.cca2,
        countryName: item.name?.common || "",
        flagPng: item.flags?.png || "",
        dialCode: `${item.idd?.root || ""}${item.idd?.suffixes?.[0] || ""}`,
      }))
      .filter((o) => o.countryName && o.dialCode)
      .sort((a, b) => a.countryName.localeCompare(b.countryName));
  }, []);

  const handleCountryChange = (_, newValue) => {
    setSelectedCountry(newValue);
    setCountryCode(newValue?.dialCode || "");
  };

  const fetchSponsorName = async (custId) => {
    if (!custId.trim()) {
      setSponsorName("");
      return;
    }
    setSponsorLoading(true);
    setSponsorName("");
    try {
      const res = await axios.post(endpoint.get_spon_name, {
        customer_id: custId,
      });
      const name = res?.data?.result?.[0]?.lgn_name || "";
      setSponsorName(name);
      if (!name) toast.error("Sponsor not found");
    } catch {
      setSponsorName("");
      toast.error("Sponsor not found");
    } finally {
      setSponsorLoading(false);
    }
  };

  useEffect(() => {
    if (refFromUrl) fetchSponsorName(refFromUrl);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fk = useFormik({
    initialValues: {
      full_name: "",
      email: "",
      mobile: "",
      password: "",
      confirm_password: "",
      referral_id: refFromUrl,
      p_lgn_pin: "",
    },
    validate: (values) => {
      const errors = {};
      if (!values.full_name.trim()) errors.full_name = "Required";
      if (!values.email.trim()) errors.email = "Required";
      if (!values.mobile.trim()) errors.mobile = "Required";
      // if (!values.referral_id.trim()) errors.referral_id = 'Required';
      if (!values.password) errors.password = "Required";
      if (values.password && values.password.length < 6)
        errors.password = "Min 6 characters";
      if (values.confirm_password !== values.password)
        errors.confirm_password = "Passwords do not match";
      if (!values.p_lgn_pin || values.p_lgn_pin.length !== 5)
        errors.p_lgn_pin = "Enter 5-digit PIN";
      return errors;
    },
    onSubmit: async (values) => {
      if (!otpVerified) {
        toast.error("Please verify your email first");
        return;
      }
      setLoading(true);
      try {
        const response = await axios.post(endpoint.member_register, {
          full_name: values.full_name.trim(),
          email: values.email.trim(),
          mobile: values.mobile.trim(),
          country_code: countryCode,
          password: values.password,
          referral_id: values.referral_id.trim(),
          lgn_pin: values.p_lgn_pin,
        });
        toast(response?.data?.message || "Registration successful");
        if (response?.data?.success) navigate("/login");
      } catch (e) {
        toast.error(e?.response?.data?.message || "Registration failed");
      } finally {
        setLoading(false);
      }
    },
  });

  const handleEmailChange = (e) => {
    fk.handleChange(e);
    if (otpSent || otpVerified) {
      setOtpSent(false);
      setOtpVerified(false);
      setOtpValue("");
    }
  };

  const handleSendOtp = async () => {
    const email = fk.values.email.trim();
    if (!email) {
      toast.error("Enter your email first");
      return;
    }
    setOtpLoading(true);
    try {
      const res = await axios.post(endpoint.send_otp, { useremail: email });
      toast.success(res?.data?.message || "OTP sent to your email");
      setOtpSent(true);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to send OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpValue.trim()) {
      toast.error("Enter the OTP");
      return;
    }
    setOtpLoading(true);
    try {
      const res = await axios.post(endpoint.verify_otp, {
        useremail: fk.values.email.trim(),
        otp: otpValue.trim(),
      });
      if (res?.data?.success) {
        toast.success(res?.data?.message || "Email verified successfully");
        setOtpVerified(true);
      } else {
        toast.error(res?.data?.message || "Invalid OTP");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Verification failed");
    } finally {
      setOtpLoading(false);
    }
  };

  const handlePinChange = (index, e) => {
    const val = e.target.value.replace(/\D/g, "").slice(-1);
    const next = [...pinDigits];
    next[index] = val;
    setPinDigits(next);
    fk.setFieldValue("p_lgn_pin", next.join(""));
    if (val && index < 4) pinRefs.current[index + 1]?.focus();
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === "Backspace" && !pinDigits[index] && index > 0) {
      pinRefs.current[index - 1]?.focus();
    }
  };

  const handlePinPaste = (e) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 5);
    if (!text) return;
    e.preventDefault();
    const next = ["", "", "", "", ""];
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setPinDigits(next);
    fk.setFieldValue("p_lgn_pin", next.join(""));
    pinRefs.current[Math.min(text.length, 4)]?.focus();
  };

  const field = (name, label, type = "text", placeholder = "", extra = {}) => (
    <div className="field-wrap">
      <label className="field-label">{label}</label>
      <div
        className={`field-row ${focused === name ? "field-row--active" : ""} ${fk.touched[name] && fk.errors[name] ? "field-row--error" : ""}`}
      >
        <svg width="16" height="16" fill="currentColor" className="field-icon">
          <use xlinkHref={`#icon-${extra.icon || "email"}`} />
        </svg>
        <input
          name={name}
          value={fk.values[name]}
          onChange={extra.readOnly ? undefined : fk.handleChange}
          onBlur={
            extra.readOnly
              ? undefined
              : (e) => {
                  setFocused("");
                  fk.handleBlur(e);
                  if (extra.onBlurCb) extra.onBlurCb(e.target.value);
                }
          }
          onFocus={extra.readOnly ? undefined : () => setFocused(name)}
          readOnly={!!extra.readOnly}
          type={type}
          placeholder={placeholder}
          autoComplete="off"
          className="field-input"
          style={{
            WebkitBoxShadow: "0 0 0 1000px transparent inset",
            WebkitTextFillColor: extra.readOnly
              ? "rgba(0,229,255,0.6)"
              : "#e0f7fa",
            textTransform: "none",
            cursor: extra.readOnly ? "default" : "text",
          }}
        />
        {extra.toggle && (
          <button type="button" onClick={extra.toggle} className="eye-btn">
            {extra.visible ? <ClosedEye /> : <EyeO />}
          </button>
        )}
      </div>
      {fk.touched[name] && fk.errors[name] && (
        <span className="field-error">{fk.errors[name]}</span>
      )}
    </div>
  );

  return (
    <div className="login-root">
      <svg xmlns="http://www.w3.org/2000/svg" style={{ display: "none" }}>
        <symbol id="icon-email" viewBox="0 0 24 24">
          <path d="M20 4H4C2.9 4 2 4.9 2 6v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
        </symbol>
        <symbol id="icon-user" viewBox="0 0 24 24">
          <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
        </symbol>
        <symbol id="icon-phone" viewBox="0 0 24 24">
          <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" />
        </symbol>
        <symbol id="icon-editPswIcon" viewBox="0 0 24 24">
          <path d="M12 1C8.676 1 6 3.676 6 7v1H4v15h16V8h-2V7c0-3.324-2.676-6-6-6zm0 2c2.276 0 4 1.724 4 4v1H8V7c0-2.276 1.724-4 4-4zm0 9a2 2 0 1 1 0 4 2 2 0 0 1 0-4z" />
        </symbol>
        <symbol id="icon-gift" viewBox="0 0 24 24">
          <path d="M20 6h-2.18c.07-.26.18-.5.18-.76 0-1.79-1.43-3.24-3.24-3.24C13.62 2 12 4.29 12 4.29S10.38 2 9.24 2C7.43 2 6 3.45 6 5.24c0 .26.11.5.18.76H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-5.24-2c.69 0 1.24.55 1.24 1.24S15.45 6.48 14.76 6.48H13c0-1.12.87-2.48 1.76-2.48zM9.24 4c.89 0 1.76 1.36 1.76 2.48H9.24C8.55 6.48 8 5.93 8 5.24 8 4.55 8.55 4 9.24 4zM11 20H4V8h7v12zm9 0h-7V8h7v12z" />
        </symbol>
      </svg>

      <div className="login-bg-grid" />
      <div className="login-glow-tl" />
      <div className="login-glow-br" />
      <div className="login-scanline" />

      <div className="login-card-wrap reg-card-wrap">
        <div className="login-card">
          <div className="card-inner-glow" />

          <div
            className="logo-wrap"
            onClick={() => navigate("/")}
            style={{ cursor: "pointer" }}
          >
            <div className="logo-ring">
              <img src={logo} alt="logo" className="logo-img" />
            </div>
            <div className="logo-pulse" />
          </div>

          <div className="title-block">
            <p className="eyebrow">NEW ACCOUNT PORTAL</p>
            <h1 className="title">Create Account</h1>
            <p className="subtitle">Join the trading platform today</p>
          </div>

          <form onSubmit={fk.handleSubmit} className="login-form">
            {field("full_name", "Full Name", "text", "Enter your full name", {
              icon: "user",
            })}

            {/* Email with Send OTP button */}
            <div className="field-wrap">
              <label className="field-label">Email Address</label>
              <div
                className={`field-row ${focused === "email" ? "field-row--active" : ""} ${fk.touched.email && fk.errors.email ? "field-row--error" : ""}`}
              >
                <svg
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="field-icon"
                >
                  <use xlinkHref="#icon-email" />
                </svg>
                <input
                  name="email"
                  value={fk.values.email}
                  onChange={handleEmailChange}
                  onBlur={(e) => {
                    setFocused("");
                    fk.handleBlur(e);
                  }}
                  onFocus={() => setFocused("email")}
                  type="email"
                  placeholder="Enter your email"
                  autoComplete="off"
                  readOnly={otpVerified}
                  className="field-input"
                  style={{
                    WebkitBoxShadow: "0 0 0 1000px transparent inset",
                    WebkitTextFillColor: otpVerified
                      ? "rgba(0,229,255,0.6)"
                      : "#e0f7fa",
                    textTransform: "none",
                    cursor: otpVerified ? "default" : "text",
                  }}
                />
                {otpVerified ? (
                  <span className="otp-badge otp-badge--ok">✓ Verified</span>
                ) : (
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={otpLoading}
                    className="send-otp-btn"
                  >
                    {otpLoading && !otpSent ? (
                      <span className="mini-spinner" />
                    ) : otpSent ? (
                      "Resend"
                    ) : (
                      "Send OTP"
                    )}
                  </button>
                )}
              </div>
              {fk.touched.email && fk.errors.email && (
                <span className="field-error">{fk.errors.email}</span>
              )}
            </div>

            {/* OTP input — shown after OTP is sent */}
            {otpSent && !otpVerified && (
              <div className="field-wrap">
                <label className="field-label">Email OTP</label>
                <div
                  className={`field-row ${otpFocused ? "field-row--active" : ""}`}
                >
                  <svg
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="field-icon"
                  >
                    <use xlinkHref="#icon-editPswIcon" />
                  </svg>
                  <input
                    value={otpValue}
                    onChange={(e) =>
                      setOtpValue(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    onFocus={() => setOtpFocused(true)}
                    onBlur={() => setOtpFocused(false)}
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter OTP sent to your email"
                    autoComplete="one-time-code"
                    className="field-input"
                    style={{
                      WebkitBoxShadow: "0 0 0 1000px transparent inset",
                      WebkitTextFillColor: "#e0f7fa",
                      textTransform: "none",
                      letterSpacing: "0.25em",
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={otpLoading || otpValue.length < 4}
                    className="send-otp-btn"
                  >
                    {otpLoading ? <span className="mini-spinner" /> : "Verify"}
                  </button>
                </div>
              </div>
            )}

            {/* Country selector */}
            <div className="field-wrap">
              <label className="field-label">Country</label>
              <Autocomplete
                size="small"
                options={countryOptions}
                value={selectedCountry}
                onChange={handleCountryChange}
                getOptionLabel={(o) => `${o.countryName} (${o.cca2})`}
                isOptionEqualToValue={(o, v) => o.cca2 === v.cca2}
                renderOption={(props, option) => (
                  <li {...props} key={option.cca2}>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      {option.flagPng && (
                        <img
                          src={option.flagPng}
                          alt={option.cca2}
                          style={{
                            width: 20,
                            height: 14,
                            borderRadius: 2,
                            objectFit: "cover",
                          }}
                        />
                      )}
                      <span>
                        {option.countryName} ({option.cca2}) {option.dialCode}
                      </span>
                    </div>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search country..."
                    sx={{
                      "& .MuiInputBase-root": {
                        background: "rgba(0,229,255,0.025)",
                        borderRadius: "12px",
                        minHeight: "48px",
                        paddingLeft: "14px !important",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "1px solid rgba(0,229,255,0.11)",
                        borderRadius: "12px",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0,229,255,0.22) !important",
                      },
                      "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0,229,255,0.48) !important",
                        boxShadow: "0 0 0 3px rgba(0,229,255,0.06)",
                      },
                      "& input": {
                        color: "#e0f7fa",
                        fontSize: "13px",
                        fontFamily: "'Sora', sans-serif",
                        padding: "0 !important",
                      },
                      "& input::placeholder": {
                        color: "rgba(0,229,255,0.22)",
                        opacity: 1,
                      },
                      "& .MuiSvgIcon-root": { color: "rgba(0,229,255,0.4)" },
                    }}
                  />
                )}
                sx={{
                  "& .MuiAutocomplete-popupIndicator": {
                    color: "rgba(0,229,255,0.4)",
                  },
                  "& .MuiAutocomplete-clearIndicator": {
                    color: "rgba(0,229,255,0.4)",
                  },
                  "& .MuiAutocomplete-listbox": {
                    background: "#041018",
                    color: "#e0f7fa",
                    border: "1px solid rgba(0,229,255,0.15)",
                  },
                  "& .MuiAutocomplete-option": {
                    fontFamily: "'Sora', sans-serif",
                    fontSize: "13px",
                  },
                  "& .MuiAutocomplete-option:hover, & .MuiAutocomplete-option.Mui-focused":
                    {
                      background: "rgba(0,229,255,0.08) !important",
                    },
                }}
                PaperComponent={({ children, ...p }) => (
                  <div
                    {...p}
                    style={{
                      background: "#041018",
                      border: "1px solid rgba(0,229,255,0.15)",
                      borderRadius: 10,
                      marginTop: 4,
                      fontFamily: "'Sora', sans-serif",
                      fontSize: 13,
                      color: "#e0f7fa",
                    }}
                  >
                    {children}
                  </div>
                )}
              />
            </div>

            {/* Mobile Number with country code prefix */}
            <div className="field-wrap">
              <label className="field-label">Mobile Number</label>
              <div
                className={`field-row ${focused === "mobile" ? "field-row--active" : ""} ${fk.touched.mobile && fk.errors.mobile ? "field-row--error" : ""}`}
              >
                <svg
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="field-icon"
                >
                  <use xlinkHref="#icon-phone" />
                </svg>
                {countryCode && (
                  <span className="dial-code-badge">{countryCode}</span>
                )}
                <input
                  name="mobile"
                  value={fk.values.mobile}
                  onChange={fk.handleChange}
                  onBlur={(e) => {
                    setFocused("");
                    fk.handleBlur(e);
                  }}
                  onFocus={() => setFocused("mobile")}
                  type="tel"
                  placeholder="Enter mobile number"
                  autoComplete="off"
                  className="field-input"
                  style={{
                    WebkitBoxShadow: "0 0 0 1000px transparent inset",
                    WebkitTextFillColor: "#e0f7fa",
                    textTransform: "none",
                  }}
                />
              </div>
              {fk.touched.mobile && fk.errors.mobile && (
                <span className="field-error">{fk.errors.mobile}</span>
              )}
            </div>

            <div className="field-wrap">
              <label className="field-label">Password</label>
              <div
                className={`field-row ${focused === "password" ? "field-row--active" : ""} ${fk.touched.password && fk.errors.password ? "field-row--error" : ""}`}
              >
                <svg
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="field-icon"
                >
                  <use xlinkHref="#icon-editPswIcon" />
                </svg>
                <input
                  name="password"
                  value={fk.values.password}
                  onChange={fk.handleChange}
                  onBlur={(e) => {
                    setFocused("");
                    fk.handleBlur(e);
                  }}
                  onFocus={() => setFocused("password")}
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password"
                  autoComplete="new-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className="field-input"
                  style={{
                    textTransform: "none",
                    WebkitBoxShadow: "0 0 0 1000px transparent inset",
                    WebkitTextFillColor: "#e0f7fa",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="eye-btn"
                >
                  {showPassword ? <ClosedEye /> : <EyeO />}
                </button>
              </div>
              {fk.touched.password && fk.errors.password && (
                <span className="field-error">{fk.errors.password}</span>
              )}
            </div>

            <div className="field-wrap">
              <label className="field-label">Confirm Password</label>
              <div
                className={`field-row ${focused === "confirm_password" ? "field-row--active" : ""} ${fk.touched.confirm_password && fk.errors.confirm_password ? "field-row--error" : ""}`}
              >
                <svg
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="field-icon"
                >
                  <use xlinkHref="#icon-editPswIcon" />
                </svg>
                <input
                  name="confirm_password"
                  value={fk.values.confirm_password}
                  onChange={fk.handleChange}
                  onBlur={(e) => {
                    setFocused("");
                    fk.handleBlur(e);
                  }}
                  onFocus={() => setFocused("confirm_password")}
                  type={showConfirm ? "text" : "password"}
                  placeholder="Repeat your password"
                  autoComplete="new-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className="field-input"
                  style={{
                    textTransform: "none",
                    WebkitBoxShadow: "0 0 0 1000px transparent inset",
                    WebkitTextFillColor: "#e0f7fa",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="eye-btn"
                >
                  {showConfirm ? <ClosedEye /> : <EyeO />}
                </button>
              </div>
              {fk.touched.confirm_password && fk.errors.confirm_password && (
                <span className="field-error">
                  {fk.errors.confirm_password}
                </span>
              )}
            </div>

            {/* Login PIN — 5 individual digit boxes */}
            <div className="field-wrap">
              <label className="field-label">Enter Pin</label>
              <div className="pin-boxes" onPaste={handlePinPaste}>
                {pinDigits.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (pinRefs.current[i] = el)}
                    value={digit}
                    onChange={(e) => handlePinChange(i, e)}
                    onKeyDown={(e) => handlePinKeyDown(i, e)}
                    onFocus={() => setFocused("p_lgn_pin")}
                    onBlur={() => {
                      setFocused("");
                      fk.setFieldTouched("p_lgn_pin", true);
                    }}
                    type="string"
                    inputMode="numeric"
                    maxLength={1}
                    autoComplete="off"
                    className={`pin-box ${fk.touched.p_lgn_pin && fk.errors.p_lgn_pin ? "pin-box--error" : ""}`}
                  />
                ))}
              </div>
              {fk.touched.p_lgn_pin && fk.errors.p_lgn_pin && (
                <span className="field-error">{fk.errors.p_lgn_pin}</span>
              )}
            </div>

            {/* Referral ID with sponsor name lookup */}
            <div className="field-wrap">
              <label className="field-label">{`Referral ID${refFromUrl ? " (auto-filled)" : ""}`}</label>
              <div
                className={`field-row ${focused === "referral_id" ? "field-row--active" : ""} ${fk.touched.referral_id && fk.errors.referral_id ? "field-row--error" : ""}`}
              >
                <svg
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="field-icon"
                >
                  <use xlinkHref="#icon-gift" />
                </svg>
                <input
                  name="referral_id"
                  value={fk.values.referral_id}
                  onChange={refFromUrl ? undefined : fk.handleChange}
                  onBlur={
                    refFromUrl
                      ? undefined
                      : (e) => {
                          setFocused("");
                          fk.handleBlur(e);
                          fetchSponsorName(e.target.value);
                        }
                  }
                  onFocus={
                    refFromUrl ? undefined : () => setFocused("referral_id")
                  }
                  readOnly={!!refFromUrl}
                  type="text"
                  placeholder="Enter referral ID"
                  autoComplete="off"
                  className="field-input"
                  style={{
                    WebkitBoxShadow: "0 0 0 1000px transparent inset",
                    WebkitTextFillColor: refFromUrl
                      ? "rgba(0,229,255,0.6)"
                      : "#e0f7fa",
                    textTransform: "none",
                    cursor: refFromUrl ? "default" : "text",
                  }}
                />
                {sponsorLoading && <span className="mini-spinner" />}
              </div>
              {fk.touched.referral_id && fk.errors.referral_id && (
                <span className="field-error">{fk.errors.referral_id}</span>
              )}
              {sponsorName && (
                <span className="sponsor-name">Sponsor: {sponsorName}</span>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !otpVerified}
              className={`submit-btn ${loading ? "submit-btn--loading" : ""} ${!otpVerified ? "submit-btn--locked" : ""}`}
            >
              <span className="submit-btn__text">
                {loading
                  ? "Creating Account…"
                  : !otpVerified
                    ? "Verify Email to Continue"
                    : "Create Account"}
              </span>
              {!loading && otpVerified && (
                <span className="submit-btn__arrow">→</span>
              )}
              {loading && <span className="submit-btn__spinner" />}
            </button>

            <div className="or-divider">
              <span className="or-line" />
              <span className="or-text">HAVE AN ACCOUNT?</span>
              <span className="or-line" />
            </div>

            <NavLink to="/login" className="register-btn">
              Sign In
            </NavLink>
          </form>

          <p className="security-note">
            <span className="security-dot" />
            Secured connection
          </p>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Sora:wght@300;400;500;600;700&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .login-root {
          min-height: 100vh;
          background: #000;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          font-family: 'Sora', sans-serif;
        }

        .login-bg-grid {
          position: fixed; inset: 0; pointer-events: none;
          background-image:
            linear-gradient(rgba(0,229,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,229,255,0.03) 1px, transparent 1px);
          background-size: 44px 44px;
          z-index: 2;
        }

        .login-glow-tl {
          position: fixed; top: -160px; left: -120px;
          width: 520px; height: 520px; border-radius: 50%;
          background: radial-gradient(circle, rgba(0,229,255,0.09) 0%, transparent 70%);
          pointer-events: none; z-index: 2;
        }
        .login-glow-br {
          position: fixed; bottom: -120px; right: -80px;
          width: 400px; height: 400px; border-radius: 50%;
          background: radial-gradient(circle, rgba(0,180,200,0.06) 0%, transparent 70%);
          pointer-events: none; z-index: 2;
        }

        .login-scanline {
          position: fixed; inset: 0; pointer-events: none;
          background: repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            rgba(0,0,0,0.06) 2px,
            rgba(0,0,0,0.06) 4px
          );
          z-index: 2;
        }

        .login-card-wrap {
          width: 100%; max-width: 420px;
          padding: 20px 16px 40px;
          position: relative; z-index: 10;
          animation: cardIn 0.55s cubic-bezier(0.22,1,0.36,1) both;
          isolation: isolate;
        }
        .reg-card-wrap {
          padding-top: 30px;
        }
        @keyframes cardIn {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .login-card {
          background: rgba(0, 0, 0, 0.92);
          border: 1px solid rgba(0,229,255,0.18);
          border-radius: 22px;
          padding: 36px 28px 28px;
          position: relative;
          box-shadow:
            0 0 0 1px rgba(0,229,255,0.06) inset,
            0 40px 80px rgba(0,0,0,0.35),
            0 0 60px rgba(0,229,255,0.06);
        }

        .card-inner-glow {
          position: absolute; top: 0; left: 50%;
          transform: translateX(-50%);
          width: 55%; height: 1px;
          background: linear-gradient(90deg, transparent, rgba(0,229,255,0.55), transparent);
        }

        .logo-wrap {
          display: flex; justify-content: center;
          margin-bottom: 22px; position: relative;
        }
        .logo-ring {
          width: 150px; height: 60px;
          background: rgba(0,229,255,0.04);
          display: flex; align-items: center; justify-content: center;
          position: relative; z-index: 1;
          box-shadow: 0 0 28px rgba(0,229,255,0.08);
        }
        .logo-img { height: auto; max-height: 48px; }
        .logo-pulse {
          position: absolute; top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          width: 160px; height: 66px;
          border: 1px solid rgba(0,229,255,0.1);
          animation: logoPulse 2.8s ease-in-out infinite;
        }
        @keyframes logoPulse {
          0%, 100% { transform: translate(-50%,-50%) scale(1); opacity: 0.7; }
          50%       { transform: translate(-50%,-50%) scale(1.14); opacity: 0; }
        }

        .title-block { text-align: center; margin-bottom: 26px; }
        .eyebrow {
          font-family: 'Space Mono', monospace;
          font-size: 9px; letter-spacing: 0.22em;
          color: rgba(0,229,255,0.38); margin-bottom: 8px;
          text-transform: uppercase;
        }
        .title {
          font-size: 22px; font-weight: 700; font-family: 'Sora', sans-serif;
          color: #e0f7fa; letter-spacing: -0.02em; margin-bottom: 5px;
        }
        .subtitle {
          font-size: 12px; color: rgba(100,180,200,0.45); font-weight: 400;
        }

        .login-form { display: flex; flex-direction: column; gap: 14px; }

        .field-wrap { display: flex; flex-direction: column; gap: 4px; }

        .field-label {
          font-family: 'Space Mono', monospace;
          font-size: 9px; letter-spacing: 0.14em;
          text-transform: uppercase; color: rgba(0,229,255,0.4);
        }

        .field-row {
          display: flex; align-items: center; gap: 10px;
          background: rgba(0,229,255,0.025);
          border: 1px solid rgba(0,229,255,0.11);
          border-radius: 12px; padding: 0 14px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .field-row:hover:not(.field-row--active):not(.field-row--error) {
          border-color: rgba(0,229,255,0.22);
        }
        .field-row--active {
          border-color: rgba(0,229,255,0.48);
          box-shadow: 0 0 0 3px rgba(0,229,255,0.06), 0 0 20px rgba(0,229,255,0.05);
        }
        .field-row--error {
          border-color: rgba(255,80,80,0.45);
          box-shadow: 0 0 0 3px rgba(255,80,80,0.06);
        }

        .field-icon { color: rgba(0,229,255,0.4); flex-shrink: 0; }

        .field-input {
          flex: 1; min-width: 0; background: transparent;
          color: #e0f7fa; font-size: 13px; font-family: 'Sora', sans-serif;
          padding: 14px 0; outline: none; border: none;
        }
        .field-input::placeholder { color: rgba(0,229,255,0.22); }

        /* Kill the browser's white/yellow autofill background */
        .field-input:-webkit-autofill,
        .field-input:-webkit-autofill:hover,
        .field-input:-webkit-autofill:focus,
        .field-input:-webkit-autofill:active,
        .pin-box:-webkit-autofill,
        .pin-box:-webkit-autofill:hover,
        .pin-box:-webkit-autofill:focus,
        .pin-box:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px #04141c inset !important;
          box-shadow: 0 0 0 1000px #04141c inset !important;
          -webkit-text-fill-color: #e0f7fa !important;
          caret-color: #e0f7fa;
          transition: background-color 5000s ease-in-out 0s;
        }

        .field-error {
          font-size: 10px; font-family: 'Space Mono', monospace;
          color: rgba(255,100,100,0.75); letter-spacing: 0.04em;
          padding-left: 2px;
        }

        .eye-btn {
          background: none; border: none; cursor: pointer;
          color: rgba(0,229,255,0.3); flex-shrink: 0;
          display: flex; align-items: center;
          transition: color 0.15s; padding: 2px;
        }
        .eye-btn:hover { color: rgba(0,229,255,0.75); }

        /* Send OTP / Verify inline button */
        .send-otp-btn {
          flex-shrink: 0;
          background: rgba(0,229,255,0.08);
          border: 1px solid rgba(0,229,255,0.22);
          border-radius: 7px;
          color: #00e5ff;
          font-family: 'Space Mono', monospace;
          font-size: 9px; font-weight: 700;
          letter-spacing: 0.08em; text-transform: uppercase;
          padding: 5px 10px; cursor: pointer;
          transition: background 0.15s, border-color 0.15s;
          display: flex; align-items: center; justify-content: center;
          min-width: 64px;
        }
        .send-otp-btn:hover:not(:disabled) {
          background: rgba(0,229,255,0.16);
          border-color: rgba(0,229,255,0.45);
        }
        .send-otp-btn:disabled { opacity: 0.45; cursor: not-allowed; }

        /* Verified badge inside email field */
        .otp-badge {
          flex-shrink: 0;
          font-family: 'Space Mono', monospace;
          font-size: 9px; letter-spacing: 0.08em;
          padding: 4px 8px; border-radius: 6px;
        }
        .otp-badge--ok {
          background: rgba(0,229,100,0.1);
          border: 1px solid rgba(0,229,100,0.3);
          color: #00e564;
        }

        /* Dial code prefix inside mobile field */
        .dial-code-badge {
          flex-shrink: 0;
          font-family: 'Space Mono', monospace;
          font-size: 12px;
          color: rgba(0,229,255,0.75);
          letter-spacing: 0.04em;
          padding-right: 8px;
          border-right: 1px solid rgba(0,229,255,0.15);
          margin-right: 4px;
          white-space: nowrap;
        }

        /* Login PIN boxes */
        .pin-boxes { display: flex; justify-content: center;  gap: 8px; }
        .pin-box {
          width: 100%; max-width: 40px; height: 42px; flex: 1; min-width: 0;
          text-align: center;
          background: rgba(0,229,255,0.025);
          border: 1px solid white;
          border-radius: 10px;
          color: #e0f7fa; font-size: 16px; letter-spacing: 0;
          font-family: 'Sora', sans-serif;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .pin-box:hover { border-color: rgba(0,229,255,0.22); }
        .pin-box:focus {
          border-color: rgba(0,229,255,0.48);
          box-shadow: 0 0 0 3px rgba(0,229,255,0.06), 0 0 20px rgba(0,229,255,0.05);
        }
        .pin-box--error {
          border-color: rgba(255,80,80,0.45);
          box-shadow: 0 0 0 3px rgba(255,80,80,0.06);
        }
        @media (max-width: 380px) {
          .pin-boxes { gap: 6px; }
          .pin-box { max-width: 32px; height: 36px; font-size: 14px; border-radius: 8px; }
        }

        /* Sponsor name hint below referral field */
        .sponsor-name {
          font-family: 'Space Mono', monospace;
          font-size: 9px; letter-spacing: 0.08em;
          color: rgba(0,229,255,0.55);
          padding-left: 2px;
        }

        /* Mini spinner for inline use */
        .mini-spinner {
          display: inline-block;
          width: 12px; height: 12px; border-radius: 50%;
          border: 1.5px solid rgba(0,229,255,0.2);
          border-top-color: #00e5ff;
          animation: spin 0.7s linear infinite;
          flex-shrink: 0;
        }

        .submit-btn {
          margin-top: 4px;
          width: 100%; height: 48px; border-radius: 12px;
          border: none; cursor: pointer;
          background: linear-gradient(90deg, #00e5ff, #00b8d4);
          color: #020c12;
          font-family: 'Space Mono', monospace;
          font-size: 12px; font-weight: 700; letter-spacing: 0.14em;
          text-transform: uppercase;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          position: relative; overflow: hidden;
          transition: transform 0.15s, box-shadow 0.2s, opacity 0.2s;
          box-shadow: 0 4px 24px rgba(0,229,255,0.22);
        }
        .submit-btn::before {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
          transform: translateX(-100%);
          transition: transform 0.5s;
        }
        .submit-btn:hover:not(:disabled)::before { transform: translateX(100%); }
        .submit-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 32px rgba(0,229,255,0.32);
        }
        .submit-btn:active:not(:disabled) { transform: translateY(0); }
        .submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .submit-btn--locked {
          background: linear-gradient(90deg, rgba(0,229,255,0.3), rgba(0,184,212,0.3));
          box-shadow: none;
        }
        .submit-btn__arrow { font-size: 16px; }
        .submit-btn__spinner {
          width: 16px; height: 16px; border-radius: 50%;
          border: 2px solid rgba(2,12,18,0.25); border-top-color: #020c12;
          animation: spin 0.7s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .or-divider {
          display: flex; align-items: center; gap: 10px; margin: 2px 0;
        }
        .or-line { flex: 1; height: 1px; background: rgba(0,229,255,0.07); }
        .or-text {
          font-family: 'Space Mono', monospace;
          font-size: 9px; letter-spacing: 0.2em; color: rgba(0,229,255,0.22);
        }

        .register-btn {
          display: flex; align-items: center; justify-content: center;
          height: 46px; border-radius: 12px;
          border: 1px solid rgba(0,229,255,0.16);
          background: rgba(0,229,255,0.03);
          color: rgba(0,229,255,0.65);
          font-family: 'Space Mono', monospace;
          font-size: 11px; font-weight: 700;
          letter-spacing: 0.1em; text-transform: uppercase;
          text-decoration: none;
          transition: all 0.2s;
        }
        .register-btn:hover {
          background: rgba(0,229,255,0.08);
          border-color: rgba(0,229,255,0.38);
          color: #00e5ff;
        }

        .security-note {
          display: flex; align-items: center; justify-content: center; gap: 7px;
          margin-top: 22px;
          font-size: 10px; color: rgba(0,229,255,0.18);
          font-family: 'Space Mono', monospace; letter-spacing: 0.06em;
        }
        .security-dot {
          width: 5px; height: 5px; border-radius: 50%;
          background: rgba(0,229,255,0.4);
          animation: blink 2.2s ease-in-out infinite;
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.15; }
        }
      `}</style>
    </div>
  );
}

export default Register;
